import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { FiUsers, FiCheck, FiHelpCircle, FiClock, FiTrendingUp, FiAlertCircle, FiRefreshCw } from 'react-icons/fi';
import Toggle from '../Toggle';
import { toast } from '../Toast';
import { SettingsHeader, SettingsActions, useSettingsSub } from './ClientSettings';
import { useClient } from './clientSettingsData';
import { to } from '../../../util/util';
import { RegistrySyncRun, RegistrySyncStatusResult } from '../../../model/RegistrySyncStatusResult';
import api from '../../../api/api';
import { startSetStock } from '../../../actions/CurrentStock';
import { ShareRegistryProvider } from '../../../model/ShareRegistryProvider';
import { IGlobalState } from '../../../model/IGlobalState';
import Stock from '../../../model/Stock';
import * as Roles from '../../../model/Roles';

// only the registries Investi reads today
const PROVIDERS: { id: ShareRegistryProvider; label: string }[] = [
    { id: ShareRegistryProvider.None, label: 'None' },
    { id: ShareRegistryProvider.Automic, label: 'Automic' },
    { id: ShareRegistryProvider.Xcend, label: 'Xcend' },
];
const providerName = (p: ShareRegistryProvider) => PROVIDERS.find(x => x.id === p)?.label ?? '';

interface Form {
    provider: ShareRegistryProvider;
    clientId: string;
    apiKey: string;
    secret: string;
    xcendIsEnabled: boolean;
    recipients: string;
    newHolderMin: string;
    largeMovement: string;
}

const toNumber = (s: string) => parseFloat(s.replace(/[^0-9.]/g, '')) || 0;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const toForm = (stock: Stock | null): Form => ({
    provider: stock?.shareRegistryProvider ?? ShareRegistryProvider.None,
    clientId: stock?.automicClientId ?? '',
    apiKey: stock?.automicApiKey ?? '',
    secret: stock?.automicClientSecret ?? '',
    xcendIsEnabled: !!stock?.xcendIsEnabled,
    recipients: stock?.shareRegistryReportRecipients ?? '',
    newHolderMin: (stock?.shareRegistryOptions?.newHolderMinValue ?? 0).toLocaleString(),
    largeMovement: (stock?.shareRegistryOptions?.largeMovementShares ?? 0).toLocaleString(),
});

// Connected once any sync has ever worked; the Last Sync card shows whether the latest one failed
const getBadge = (f: Form, status: RegistrySyncStatusResult): { label: string; ok: boolean } => {
    if (f.provider === ShareRegistryProvider.None)
        return { label: 'Not connected', ok: false };
    if (status.hasSuccessfulSync)
        return { label: 'Connected', ok: true };
    return { label: 'Not synced yet', ok: false };
};

const formatAttempt = (run: RegistrySyncRun, format: string) => moment.utc(run.lastAttemptUtc).local().format(format);

const getRunNote = (run: RegistrySyncRun) => {
    if (!run.success)
        return 'Failed';
    if (run.retries > 0)
        return `Retried ${run.retries === 1 ? 'once' : `${run.retries} times`}`;
    return 'OK';
};

// label: the visible field label, so the input and its Show button are named for screen readers
const Secret: React.FC<{ label: string; value: string; onChange: (v: string) => void }> = ({ label, value, onChange }) => {
    const [shown, setShown] = useState(false);
    return <div className="value-box input-box">
        <input type={shown ? 'text' : 'password'} aria-label={label} value={value} onChange={e => onChange(e.target.value)} autoComplete="off" />
        <button type="button" className="link" aria-label={`${shown ? 'Hide' : 'Show'} ${label}`} onClick={() => setShown(!shown)}>{shown ? 'Hide' : 'Show'}</button>
    </div>;
};

const RegistryConnection: React.FC = () => {
    const history = useHistory();
    const dispatch = useDispatch();
    const { stock, ticker, exchange } = useClient();
    const user = useSelector((state: IGlobalState) => state.user);
    const isAdmin = user.roles.hasOwnProperty(Roles.Admin);
    const sub = useSettingsSub();
    const [form, setForm] = useState<Form>(() => toForm(stock));
    const [saved, setSaved] = useState<Form>(() => toForm(stock));
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setForm(toForm(stock));
        setSaved(toForm(stock));
    }, [stock]);

    // null while loading
    const [status, setStatus] = useState<RegistrySyncStatusResult | null>(null);
    useEffect(() => {
        if (!stock?.id) return;
        let cancelled = false;
        setStatus(null);
        api().getRegistrySyncStatus(stock.id).then(r => { if (!cancelled) setStatus(r); });
        return () => { cancelled = true; };
    }, [stock?.id]);
    const [syncing, setSyncing] = useState(false);
    const [testing, setTesting] = useState(false);
    const [test, setTest] = useState<{ ok: boolean; text: string } | null>(null);
    useEffect(() => setTest(null), [form.provider, form.clientId, form.apiKey, form.secret]);
    const dirty = JSON.stringify(form) !== JSON.stringify(saved);

    const set = <K extends keyof Form>(field: K, value: Form[K]) => setForm(f => ({ ...f, [field]: value }));

    // the timer's daily run, now; the cards reload from the run it records
    const syncNow = async () => {
        setSyncing(true);
        const [err, result] = await to(api().syncRegistryNow(ticker, exchange));
        const refreshed = await api().getRegistrySyncStatus(stock!.id);
        setStatus(refreshed);
        setSyncing(false);
        if (err || !result.success) {
            toast.error(`Sync failed: ${err?.message || result?.message || 'unknown error'}`);
            return;
        }
        toast.success(`Sync finished for ${moment(refreshed.syncNowDate).format('ddd D MMM')} · ${refreshed.lastSync?.holderCount ?? 0} holders`);
    };

    // the form's values, unsaved; the smallest provider call that proves they work
    const testConnection = async () => {
        setTesting(true);
        const [err, result] = await to(api().testRegistryConnection(ticker, exchange, {
            provider: form.provider,
            automicClientId: form.clientId,
            automicApiKey: form.apiKey,
            automicClientSecret: form.secret,
        }));
        setTesting(false);
        const name = providerName(form.provider);
        if (err || !result.success) {
            const text = `Couldn’t connect to ${name}: ${err?.message || result?.message || 'unknown error'}`;
            setTest({ ok: false, text });
            toast.error(text);
            return;
        }
        const text = result.issuerName ? `Connected to ${name} · issuer ${result.issuerName}` : `Connected to ${name}`;
        setTest({ ok: true, text });
        toast.success(text);
    };

    const testButton = <button type="button" className="btn-outline with-icon" disabled={testing} onClick={testConnection}>
        <FiCheck /> {testing ? 'Testing…' : 'Test Connection'}
    </button>;
    const testNote = test && <div className={`registry-note ${test.ok ? 'ok' : 'error'}`} role="status">
        {test.ok ? <FiCheck /> : <FiAlertCircle />}<span>{test.text}</span>
    </div>;

    const save = async () => {
        const recipients = form.recipients.split(/[,;\s]+/).map(x => x.trim()).filter(x => x);
        const bad = recipients.find(x => !EMAIL.test(x));
        if (bad) {
            toast.error(`${bad} isn’t an email address`);
            return;
        }
        setSaving(true);
        const result = await api().saveShareRegistrySettings(ticker, exchange, {
            shareRegistryProvider: form.provider,
            automicClientId: form.clientId,
            automicApiKey: form.apiKey,
            automicClientSecret: form.secret,
            xcendIsEnabled: form.xcendIsEnabled,
        });
        if (!result.success) {
            setSaving(false);
            toast.error(result.errorMessage || 'Couldn’t save the registry settings');
            return;
        }
        if (form.recipients !== saved.recipients || form.newHolderMin !== saved.newHolderMin || form.largeMovement !== saved.largeMovement) {
            const badges = await api().saveShareRegistryHoldingsMovementReportSettings(ticker, exchange, {
                shareRegistryReportRecipients: recipients.join(', '),
                shareRegistryOptions: { ...stock?.shareRegistryOptions,
                    newHolderMinValue: toNumber(form.newHolderMin), largeMovementShares: toNumber(form.largeMovement) },
            });
            if (!badges.success) {
                setSaving(false);
                toast.error(badges.errorMessage || 'Couldn’t save the movement report settings');
                return;
            }
        }
        setSaving(false);
        toast.success('Registry settings saved');
        dispatch(startSetStock(`${ticker}:${exchange}`));
    };

    const badge = status && getBadge(saved, status);
    const lastSync = status?.lastSync;
    const actions = { dirty, saving, onSave: save, onDiscard: () => setForm(saved) };

    return <>
        <SettingsHeader title="Registry Connection" sub={sub} {...actions} />

        {syncing && <div className="sync-banner">
            <div className="head">
                <span className="icon-tile"><FiRefreshCw className="spin" /></span>
                <div className="text">
                    <div className="title">Pulling the register from {providerName(saved.provider)}…</div>
                    <div className="hint">This can take a minute. You can keep working on this page — we'll tell you when it's done.</div>
                </div>
            </div>
        </div>}

        <div className="cs-row">
            <div className="cs-main">
                <section className="card">
                    <div className="section-title">
                        <span className="icon-tile"><FiUsers /></span>
                        <div className="text">
                            <h2>Share Registry Provider</h2>
                            <div className="hint">Investi reads the register nightly from your registry's API</div>
                        </div>
                        {badge && <span className={`badge ${badge.ok ? 'ok' : 'warn'}`}>{badge.label}</span>}
                    </div>

                    <div className="options four" role="radiogroup" aria-label="Share registry provider">
                        {PROVIDERS.map(p => <button key={p.id} type="button" role="radio" aria-checked={form.provider === p.id}
                            className={`option${form.provider === p.id ? ' selected' : ''}`} onClick={() => set('provider', p.id)}>
                            <span className="radio" /> <strong>{p.label}</strong>
                        </button>)}
                    </div>

                    {form.provider === ShareRegistryProvider.Xcend && <>
                        {!form.xcendIsEnabled && <div className="registry-note">
                            <FiAlertCircle />
                            <span>Contact support to set this up — Xcend needs to enable it on their side.</span>
                        </div>}
                        {isAdmin && <div className="setting-row">
                            <div className="text">
                                <div className="title">Enabled</div>
                                <div className="hint">Admin only. Turn on once Xcend has approved this client.</div>
                            </div>
                            <Toggle label="Xcend enabled" checked={form.xcendIsEnabled} onChange={v => set('xcendIsEnabled', v)} />
                        </div>}
                        <div className="buttons">{testButton}</div>
                        {testNote}
                    </>}

                    {form.provider === ShareRegistryProvider.Automic && <>
                    <div className="fields">
                        <label className="field">
                            <span className="label">Client ID</span>
                            <input value={form.clientId} onChange={e => set('clientId', e.target.value)} />
                        </label>
                        <div className="field">
                            <span className="label">API key</span>
                            <Secret label="API key" value={form.apiKey} onChange={v => set('apiKey', v)} />
                        </div>
                        <div className="field">
                            <span className="label">Client secret</span>
                            <Secret label="Client secret" value={form.secret} onChange={v => set('secret', v)} />
                        </div>
                        <div className="field">
                            <span className="label">Issuer code at registry</span>
                            <div className="value-box subtle"><span>{ticker}</span></div>
                        </div>
                    </div>

                    <div className="buttons">
                        {testButton}
                        <button type="button" className="btn-outline with-icon" onClick={() => history.push('/help')}><FiHelpCircle /> Where Do I Find These?</button>
                    </div>
                    {testNote}
                    </>}
                </section>

                <section className="card">
                    <div className="section-title"><span className="icon-tile"><FiClock /></span><h2>Sync Schedule</h2></div>
                    <div className="setting-row">
                        <div className="text">
                            <div className="title">Daily sync</div>
                            <div className="hint">Every business day, Sydney time, once the previous day's register is available</div>
                        </div>
                        <strong>{status?.syncTimeSydney ?? '—'}</strong>
                    </div>
                    <div className="fields">
                        <label className="field">
                            <span className="label stacked">Movement report recipients <em>the daily holdings movement report is emailed to these addresses, comma separated; leave empty for no email</em></span>
                            <input value={form.recipients} onChange={e => set('recipients', e.target.value)} />
                        </label>
                    </div>
                </section>

                <section className="card">
                    <div className="section-title"><span className="icon-tile"><FiTrendingUp /></span><h2>Movement Badges</h2></div>
                    <div className="fields">
                        <label className="field">
                            <span className="label stacked">New holder minimum value (A$) <em>buys below this value don't get a NEW or RETURNING badge</em></span>
                            <input value={form.newHolderMin} onChange={e => set('newHolderMin', e.target.value)} />
                        </label>
                        <label className="field">
                            <span className="label stacked">Large movement threshold (shares) <em>highlights big buys and sells</em></span>
                            <input value={form.largeMovement} onChange={e => set('largeMovement', e.target.value)} />
                        </label>
                    </div>
                </section>
            </div>

            <div className="cs-side">
                <section className="card">
                    <div className="section-title"><span className="icon-tile"><FiCheck /></span><h2>Last Sync</h2></div>
                    <div className="kv">
                        {status && !lastSync && <div><span>Completed</span><strong>No syncs yet</strong></div>}
                        {lastSync && <>
                            <div><span>Completed</span><strong>{formatAttempt(lastSync, 'DD MMM YYYY, h:mm a')}</strong></div>
                            <div><span>Status</span><strong className={lastSync.success ? '' : 'failed'} title={lastSync.error ?? undefined}>
                                {lastSync.success ? 'OK' : `Failed${lastSync.error ? `: ${lastSync.error}` : ''}`}</strong></div>
                            <div><span>Holders</span><strong>{lastSync.holderCount ?? '—'}</strong></div>
                            <div><span>Movements</span><strong>{lastSync.movementCount ?? '—'}</strong></div>
                        </>}
                        <div><span>Next sync</span><strong>{status?.nextSyncSydney ? moment(status.nextSyncSydney).format('ddd DD MMM, h:mm a') : 'Not scheduled'}</strong></div>
                    </div>
                    <button type="button" className="btn-dark" onClick={syncNow} disabled={syncing || saved.provider === ShareRegistryProvider.None}>
                        <FiTrendingUp /> {syncing ? 'Syncing…' : 'Sync Now'}
                    </button>
                    {status && saved.provider !== ShareRegistryProvider.None && <p className="sync-now-hint">Syncs the register and the holdings movement report for {moment(status.syncNowDate).format('ddd D MMM')}, the last business day.</p>}
                </section>

                <section className="card">
                    <div className="section-title"><span className="icon-tile"><FiClock /></span><h2>Recent Syncs</h2></div>
                    <div className="sync-list">
                        {status && status.recentSyncs.length === 0 && <div><span>No syncs yet</span></div>}
                        {status?.recentSyncs.map(s => <div key={s.date} className={s.success && s.retries === 0 ? 'ok' : 'warn'} title={s.error ?? undefined}>
                            {s.success ? <FiCheck /> : <FiAlertCircle />}
                            <span>{formatAttempt(s, 'DD MMM · h:mm a')}</span>
                            <em>{getRunNote(s)}</em>
                        </div>)}
                    </div>
                </section>
            </div>
        </div>

        <div className="action-bottom"><SettingsActions {...actions} /></div>
    </>;
};

export default RegistryConnection;
